// Synthetic expense rows for sibling V3Offers — mirrors the server-side rule
// in V3Budgets::Base#sibling_guarantee_expenses (O-2993).
//
//   - Suppressed when the viewer's performer is explicitly a support offer
//     (supports see only their own deal).
//   - Filters out the current offer and any sibling with a zero or blank
//     dollar_amount.
//   - Artist-name fallback chain matches the server: performer.artistable.name
//     → primary_performer.artistable.name → artist_name → "Artist".
//
// The same shape is consumed by two paths in V3FinancePage:
//
//   1. Math path — prepended into budgetForMath.expense_line_items so
//      FinancialSummaryMath sees sibling guarantees as ordinary expenses.
//   2. UI path — passed as siblingRows to <ExpensesSection> and rendered as
//      read-only rows in the Expenses table. The row carries
//      isAutoGenerated: true so every existing locked-input branch in
//      BudgetLineItemSection picks it up without modification, and
//      isSiblingGuarantee: true so the link-arrow icon + click-to-navigate
//      handler can target it specifically.

export const SIBLING_ROW_PREFIX = 'sibling:';

const ACT_TYPE_SUPPORT = 'support';

const resolveArtistName = (sibling) => (
  sibling?.performer?.artistable?.name ||
  sibling?.primary_performer?.artistable?.name ||
  sibling?.artist_name ||
  'Artist'
);

// Some V3Offers carry the act_type on `performer` (the direct association
// when performer_id is set on the offer record), others carry it on
// `primary_performer` (the has_one :as => :performable association used by
// the multi-artist V3 flow). The act_type guard accepts either shape so a
// support offer's preview stays sibling-free in both cases.
const resolveActType = (offer) => (
  offer?.performer?.act_type ||
  offer?.primary_performer?.act_type ||
  offer?.act_type ||
  null
);

export function buildSiblingGuaranteeExpenses(offer, eventOffers) {
  if (!offer) return [];
  if (resolveActType(offer) === ACT_TYPE_SUPPORT) return [];

  return (eventOffers || [])
    .filter((o) => o && o.id !== offer.id)
    .map((sibling) => {
      const amount = parseFloat(sibling.dollar_amount);
      return Number.isFinite(amount) && amount > 0 ? { sibling, amount } : null;
    })
    .filter(Boolean)
    .map(({ sibling, amount }) => ({
      id: `${SIBLING_ROW_PREFIX}${sibling.id}`,
      uuid: `${SIBLING_ROW_PREFIX}${sibling.id}`,
      name: resolveArtistName(sibling),
      category: 'expenses',
      calc_type: 'fixed',
      left_hand: amount,
      operator: '*',
      right_hand: 1,
      // BudgetLineItemSection renders an offer + forecast + actuals row per
      // line item and feeds these values straight into CurrencyInput. Text
      // Mask crashes on null/undefined/NaN, so every field a row column
      // reads needs a concrete number/string default.
      left_hand_forecast: amount,
      right_hand_forecast: 1,
      left_hand_actual: amount,
      right_hand_actual: 1,
      show_in_offer: true,
      // Mark as auto-generated so the existing read-only/locked-input
      // branches in BudgetLineItemSection apply without modification.
      isAutoGenerated: true,
      // Additional marker the UI layer uses to render the link-arrow icon
      // and wire up click-to-navigate.
      isSiblingGuarantee: true,
      siblingOfferId: sibling.id,
    }));
}
